import { useCallback, useEffect, useRef, useState } from 'react';
import toolkitService from '../../services/ToolkitService';
import { writeWorkbook, downloadBlob } from '../../utils/xlsx';

// "Run on server" for a Toolkit batch (useToolkitBatch `server`): the grid's rows handed to the toolkit-jobs Step
// Function (lambda jobs.js) instead of being sent one by one from this tab — it keeps going when the tab is closed.
//
// Each row becomes exactly the request the screen would have sent ({ area, op, args } from its `request`), so the
// server runs what the browser would have run. Results come back the same way: the job's rows are polled and each one
// is put on its grid row through the screen's own `interpret`, as if it had run here.
//
// THE JOB BELONGS TO THE GRID IT RAN ON. Its fingerprint is the rows' keys (server.keyOf) when it started; a job only
// writes its results onto a grid with the same rows. The latest job of this screen is picked up when the screen opens
// (or the one named by ?job=<id>, the completion email's link), so a run started yesterday shows its outcome today.

const POLL_MS = 5000;
const ACTIVE = new Set(['STARTING', 'RUNNING']);

// A short stable hash of the grid's row keys: equal rows → equal fingerprint, any other grid → a different one.
export const fingerprintOf = (keys) => {
    let h1 = 0x811c9dc5;
    let h2 = 0;
    const text = `${keys.length}|${keys.join('\u0001')}`;
    for (let i = 0; i < text.length; i++) {
        h1 = Math.imul(h1 ^ text.charCodeAt(i), 16777619) >>> 0;
        h2 = (h2 * 31 + text.charCodeAt(i)) >>> 0;
    }
    return `${keys.length}-${h1.toString(16)}-${h2.toString(16)}`;
};

const errorOf = (e) => e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Request failed';
const jobIdFromUrl = () => {
    try { return parseInt(new URLSearchParams(window.location.search).get('job'), 10) || null; } catch (_) { return null; }
};

const useServerJob = ({ ctx, server, rowsRef, patch, request, interpret, doneStatuses }) => {
    const [job, setJob] = useState(null);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    // Rows of the job waiting for someone to run a script and upload its result (no SyncConnect): { i, key, queryId, title }.
    const [waiting, setWaiting] = useState([]);
    const cursorRef = useRef(null);
    const attachedRef = useRef(null);

    const enabled = !!(ctx && server && request);
    const screen = server?.screen;
    const keyOf = server?.keyOf || ((row, i) => String(i));
    const gridPrint = useCallback(() => fingerprintOf(rowsRef.current.map((r, i) => String(keyOf(r, i)))), [rowsRef, keyOf]);
    const matches = !!job && !!rowsRef.current.length && job.fingerprint === gridPrint();

    // One job row's outcome onto its grid row, through the screen's interpret.
    const apply = useCallback((items) => {
        setWaiting((list) => {
            const m = new Map(list.map((w) => [w.i, w]));
            for (const it of items) {
                if (it.status === 'waiting' && it.result?.delivery === 'UPLOAD') m.set(it.i, { i: it.i, key: it.key, queryId: it.result.queryId, title: it.result.title });
                else m.delete(it.i);
            }
            return [...m.values()];
        });
        for (const it of items) {
            const row = rowsRef.current[it.i];
            if (!row || it.status === 'pending') continue;
            if (it.status === 'processing' || it.status === 'waiting') { patch(it.i, { status: it.status, errorMessage: it.errorMessage || null }); continue; }
            const fields = it.result && interpret ? interpret(it.result, row) : (it.result || {});
            patch(it.i, { ...fields, status: fields.status || it.status, errorMessage: fields.errorMessage ?? it.errorMessage ?? null });
        }
    }, [rowsRef, patch, interpret]);

    // Every row result of the job, page by page — for applying a finished job, and for the download.
    const allItems = useCallback(async (jobId) => {
        const out = [];
        let after = -1;
        for (;;) {
            const { data } = await toolkitService.jobs(ctx, 'items', { jobId, after });
            out.push(...data.items);
            if (data.next == null) return out;
            after = data.next;
        }
    }, [ctx]);

    // Pick up this screen's latest job (or the one the email linked) once there are rows to put it on.
    useEffect(() => {
        setJob(null);                       // a different screen (or tab of one) has its own jobs
        setWaiting([]);
        cursorRef.current = null;
        if (!enabled) return;
        const wanted = jobIdFromUrl();
        const pick = wanted
            ? toolkitService.jobs(ctx, 'get', { jobId: wanted }).then(({ data }) => (data.job.screen === screen ? data.job : null))
            : toolkitService.jobs(ctx, 'list', { screen, limit: 1 }).then(({ data }) => data.items[0] || null);
        pick.then((j) => { if (j) setJob(j); }).catch(() => {});
    }, [enabled, ctx, screen]);

    // A finished job matching the grid: put all its results on once.
    useEffect(() => {
        if (!job || !matches || ACTIVE.has(job.status) || attachedRef.current === job.id) return;
        attachedRef.current = job.id;
        allItems(job.id).then(apply).catch((e) => setMessage(errorOf(e)));
    }, [job, matches, allItems, apply]);

    // A running job: poll its progress, and its changed rows onto the grid.
    useEffect(() => {
        if (!job || !ACTIVE.has(job.status)) return undefined;
        const t = setTimeout(async () => {
            try {
                const { data } = await toolkitService.jobs(ctx, 'get', { jobId: job.id, since: cursorRef.current });
                cursorRef.current = data.cursor;
                if (job.fingerprint === gridPrint()) apply(data.items);
                setJob(data.job);
            } catch (e) {
                setMessage(errorOf(e));
                setJob({ ...job });          // try again next tick
            }
        }, POLL_MS);
        return () => clearTimeout(t);
    }, [job, ctx, apply, gridPrint]);

    const runOnServer = useCallback(async () => {
        setBusy(true);
        setMessage('Preparing the rows…');
        try {
            const items = [];
            rowsRef.current.forEach((row, i) => {
                if (doneStatuses.has(row.status)) return;
                const q = request(row, {});
                if (q.result) { patch(i, q.result); return; }          // decided here, without a request
                items.push({ i, key: String(keyOf(row, i)), area: q.area, op: q.op, args: q.args, side: q.side,
                    save: server.save ? server.save(row) : undefined });
            });
            if (!items.length) { setMessage('Every row has already succeeded or been skipped.'); return; }
            const file = new File([JSON.stringify(items)], `${server.screen}-job.json`, { type: 'application/json' });
            const itemsKey = await toolkitService.upload(file);
            // extra(): what this run adds — a v5 write's confirmation (the typed project name), a flow (AR Accounts' re-send).
            const extra = server.extra ? server.extra() : {};
            const { data } = await toolkitService.jobs(ctx, 'start', {
                screen: server.screen, title: server.title, screenPath: window.location.pathname, itemsKey,
                fingerprint: gridPrint(), confirm: extra.confirm,
                options: { afterItem: server.afterItem, tool: server.tool, ...(extra.options || {}) },
            });
            cursorRef.current = null;
            attachedRef.current = data.job.id;
            setJob(data.job);
            setMessage(`Started on the server: ${items.length} row(s). You can close this page — you will get an email when it finishes.`);
        } catch (e) {
            setMessage(errorOf(e));
        } finally {
            setBusy(false);
        }
    }, [ctx, server, request, patch, rowsRef, keyOf, gridPrint, doneStatuses]);

    const act = useCallback(async (op, done) => {
        if (!job) return;
        setBusy(true);
        try {
            const { data } = await toolkitService.jobs(ctx, op, { jobId: job.id });
            cursorRef.current = null;
            setJob(data.job);
            setMessage(done(data.job));
        } catch (e) {
            setMessage(errorOf(e));
        } finally {
            setBusy(false);
        }
    }, [ctx, job]);
    const stop = useCallback(() => act('stop', (j) => `Stopped — ${j.pending} row(s) not run. Resume runs them.`), [act]);
    const resume = useCallback(() => act('resume', () => 'Resumed on the server.'), [act]);

    const download = useCallback(async () => {
        if (!job) return;
        try {
            const items = await allItems(job.id);
            downloadBlob(await writeWorkbook([{ name: 'Job', columns: ['Row', 'Record', 'Result', 'Message'],
                rows: items.map((x) => [x.i + 1, x.key || '', x.status, x.errorMessage || '']) }]),
            `${job.screen}-job-${job.id}.xlsx`);
        } catch (e) {
            setMessage(errorOf(e));
        }
    }, [job, allItems]);

    return {
        enabled, job, matches, active: !!job && ACTIVE.has(job.status), busy, message, waiting,
        runOnServer, stop, resume, download,
    };
};

export default useServerJob;
